/**
 * PEOC Arrivals surface (W4b — coordination-tab council ruling A.6) — the ONE first-notice
 * inbox on the Coordination rail. It receives three first-notice classes: incoming
 * emergency DECLARATIONS, the FN PEOC/ISC dual-notify (a badge on a declaration), and
 * AREA-COMMAND establishments — ADJACENT TO but SEPARATE FROM the urgent life-safety queue
 * (a declaration notice is a coordination arrival, not a hazard row).
 *
 * An arrival is ACKNOWLEDGED — the PEOC RECEIVES + coordinates support (App A #46 Liaison);
 * it does NOT approve, command, adjudicate, or assume authority (PERP §6.8.1.2). The action
 * reads "Acknowledge receipt"; the post-ack state reads "Received by PEOC". This is NOT an
 * App A #9 Check-in. Recording a receipt never mutates the declaration/incident.
 *
 * FN PARITY: a First-Nation Band Council declaration renders as a FULL PEER of a municipal
 * one — same card, weight, verb. The ONLY FN-specific elements are the "ISC also notified"
 * chip and the "Band Council Resolution — <First Nation>" attribution.
 *
 * Polled on the shared 30s cadence; reports the count of NOT-yet-acknowledged arrivals up
 * for the sub-tab badge. All operator copy comes from peocArrivalsVocabulary.ts (pinned).
 */
import { useEffect, useState } from 'react';
import type { PeocArrival } from '@tsr/shared-types';
import { acknowledgePeocArrival, fetchPeocArrivals } from './api';
import { getSessionId } from './lib/operatorActions';
import { showToast } from './lib/toast';
import {
  ACKNOWLEDGE_RECEIPT_LABEL,
  AWAITING_RECEIPT_LABEL,
  ISC_ALSO_NOTIFIED_LABEL,
  PEOC_ARRIVALS_EMPTY,
  PEOC_ARRIVALS_FRAMING_CAPTION,
  PEOC_ARRIVALS_SURFACE_TITLE,
  RECEIVED_BY_PEOC_LABEL,
  arrivalTypeLabel,
  authorityKindLabel,
  bandCouncilResolutionAttribution,
  hazardTypeLabel,
  receivedByTooltip,
} from './peocArrivalsVocabulary';

/** Passive re-poll cadence (mirrors the AssistQueue / AnomalyQueue 30s tick). */
export const PEOC_ARRIVALS_POLL_MS = 30_000;

/** Count of arrivals not yet acknowledged (the actionable badge count). */
export function unacknowledgedCount(rows: PeocArrival[]): number {
  return rows.filter((r) => r.acknowledged_at === null).length;
}

/**
 * Pure: triage order — NOT-yet-acknowledged first, then most-recent (arrived_at DESC).
 * Stable (the server already returns arrived_at DESC), so acknowledged rows keep recency
 * order beneath the open ones. Reused by the panel tests.
 */
export function sortArrivals(rows: PeocArrival[]): PeocArrival[] {
  return [...rows].sort((a, b) => {
    const aOpen = a.acknowledged_at === null ? 0 : 1;
    const bOpen = b.acknowledged_at === null ? 0 : 1;
    if (aOpen !== bOpen) return aOpen - bOpen;
    return b.arrived_at.localeCompare(a.arrived_at);
  });
}

/** Compact local time for a card (best-effort; falls back to the ISO string). */
function formatArrivedAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function PeocArrivalsPanel({
  onActiveCountChange,
}: {
  /** Report the count of NOT-yet-acknowledged arrivals up for the sub-tab badge. */
  onActiveCountChange?: (count: number) => void;
}): JSX.Element {
  const [rows, setRows] = useState<PeocArrival[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [acking, setAcking] = useState<string | null>(null);

  useEffect(() => {
    const id = setInterval(() => setRefreshKey((k) => k + 1), PEOC_ARRIVALS_POLL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const ctl = new AbortController();
    void fetchPeocArrivals(ctl.signal)
      .then((r) => setRows(sortArrivals(r)))
      .catch(() => undefined);
    return () => ctl.abort();
  }, [refreshKey]);

  useEffect(() => {
    onActiveCountChange?.(unacknowledgedCount(rows));
  }, [rows, onActiveCountChange]);

  const acknowledge = (a: PeocArrival): void => {
    const key = `${a.source_type}:${a.source_id}`;
    setAcking(key);
    void acknowledgePeocArrival(a.source_type, a.source_id, getSessionId())
      .then(() => setRefreshKey((k) => k + 1))
      .catch(() => showToast('Could not record that receipt.', { variant: 'error' }))
      .finally(() => setAcking((cur) => (cur === key ? null : cur)));
  };

  return (
    <div className="px-3 py-3" data-testid="peoc-arrivals-panel">
      <div className="mb-2">
        <h3 className="text-[12px] font-semibold text-slate-200">{PEOC_ARRIVALS_SURFACE_TITLE}</h3>
        {/* ALWAYS-VISIBLE framing caption — receipt-not-command (council A.6). */}
        <p
          data-testid="peoc-arrivals-framing-caption"
          className="mt-1 text-[10px] leading-relaxed text-slate-500"
        >
          {PEOC_ARRIVALS_FRAMING_CAPTION}
        </p>
      </div>

      {rows.length === 0 ? (
        <p className="text-[12px] text-slate-500" data-testid="peoc-arrivals-empty">
          {PEOC_ARRIVALS_EMPTY}
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((a) => {
            const key = `${a.source_type}:${a.source_id}`;
            const received = a.acknowledged_at !== null;
            const kindLabel = authorityKindLabel(a.authority_kind);
            const hazard = hazardTypeLabel(a.hazard_type);
            const isFn = a.authority_kind === 'first_nation';
            return (
              <li
                key={key}
                data-testid="peoc-arrival-row"
                data-source-type={a.source_type}
                className={`rounded border-l-2 border-y border-r px-3 py-2 ${
                  received
                    ? 'border-slate-700 border-l-slate-600 bg-slate-800/30'
                    : 'border-slate-700 border-l-teal-400 bg-slate-800/50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="rounded bg-slate-700/70 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-200">
                    {arrivalTypeLabel(a.source_type)}
                  </span>
                  {kindLabel && (
                    <span
                      data-testid="peoc-arrival-authority-kind"
                      className="rounded border border-slate-600 px-1.5 py-0.5 text-[10px] text-slate-300"
                    >
                      {kindLabel}
                    </span>
                  )}
                  {hazard && (
                    <span className="rounded border border-slate-700 px-1.5 py-0.5 text-[10px] text-slate-400">
                      {hazard}
                    </span>
                  )}
                  {isFn && a.isc_notified_at && (
                    <span
                      data-testid="peoc-arrival-isc-badge"
                      className="rounded border border-indigo-500/60 bg-indigo-500/10 px-1.5 py-0.5 text-[10px] text-indigo-300"
                    >
                      {ISC_ALSO_NOTIFIED_LABEL}
                    </span>
                  )}
                </div>

                <p className="mt-1 truncate text-[13px] font-medium text-slate-100">{a.title}</p>

                {/* FN-only declaring-authority attribution (parity: municipal has no BCR). */}
                {isFn && (
                  <p
                    data-testid="peoc-arrival-bcr-attribution"
                    className="mt-0.5 text-[11px] text-slate-400"
                  >
                    {bandCouncilResolutionAttribution(a.title)}
                  </p>
                )}

                <div className="mt-1 flex items-center gap-2 text-[10px] text-slate-500">
                  {a.reference && <span>{a.reference}</span>}
                  <span>{formatArrivedAt(a.arrived_at)}</span>
                </div>

                <div className="mt-2">
                  {received ? (
                    <span
                      data-testid="peoc-arrival-received"
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-400"
                      title={receivedByTooltip(a.acknowledged_by, a.acknowledged_at ?? '')}
                    >
                      <span aria-hidden>✓</span> {RECEIVED_BY_PEOC_LABEL}
                    </span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        data-testid="peoc-arrival-acknowledge-btn"
                        disabled={acking === key}
                        onClick={() => acknowledge(a)}
                        className="rounded border border-teal-500/60 bg-teal-500/10 px-2 py-1 text-[11px] font-medium text-teal-200 hover:bg-teal-500/20 disabled:opacity-50"
                      >
                        {ACKNOWLEDGE_RECEIPT_LABEL}
                      </button>
                      <span className="text-[10px] text-slate-500">{AWAITING_RECEIPT_LABEL}</span>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
